import { PageRange, TaskState, WorkloadTask } from 'shovel-lib/types';

import { CalendarCourse } from '@state/calendar/types';
import { DEFAULT_SECONDS_PER_PAGE, DEFAULT_TASK_CATEGORY_EMOJI } from '@utils/constants/task';
import { categoryMinutesOf } from '@utils/categoryTime';

import { PAGE_PRESETS, RATE_PRESETS_SECONDS, TIME_PRESETS } from './constants';
import { EstimateTasksRequest } from '@state/task/types';

import { EstimatorCategory, EstimatorOriginals, EstimatorReadingSource, EstimatorState, EstimatorTask } from './types';

export const formatMinutes = (minutes: number | null | undefined): string | null => {
  if (minutes == null) return null;
  const rounded = Math.round(minutes);
  if (rounded % 60 === 0) return `${rounded / 60}h`;
  if (rounded < 60) return `${rounded}m`;
  return `${Math.floor(rounded / 60)}h ${rounded % 60}m`;
};

// Times the way the pills write them, so a card shows one number one way: 45m, 1.5h, 28h, and 2h 45m when it isn't
// a whole or half hour.
export const formatPillMinutes = (minutes: number | null | undefined): string | null => {
  if (minutes == null) return null;
  const rounded = Math.round(minutes);
  if (rounded < 60) return `${rounded}m`;
  if (rounded % 30 === 0) return `${rounded / 60}h`;
  return formatMinutes(rounded);
};

export const formatRateLabel = (secondsPerPage: number): string => {
  const totalSeconds = Math.round(secondsPerPage);
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  if (m > 0 && s > 0) return `${m}m ${s}s/p`;
  if (m > 0) return `${m}m/p`;
  return `${s}s/p`;
};

export const readingMinutes = (pages: number | null, secondsPerPage: number): number | null => {
  if (pages == null) return null;
  return (pages * secondsPerPage) / 60;
};

export const isTimePreset = (minutes: number | null): boolean => minutes != null && TIME_PRESETS.includes(minutes);

// The pill to recommend from what finished tasks took on average: the next preset up, since a student's own time tends
// to run over a guess, as long as it's within half again of the average (5h 05m doesn't jump to 10h); otherwise the
// average itself, rounded up to a quarter hour.
export const recommendedMinutes = (averageMinutes: number): number => {
  const preset = TIME_PRESETS.find(value => value >= averageMinutes);
  return preset != null && preset <= averageMinutes * 1.5 ? preset : Math.ceil(averageMinutes / 15) * 15;
};

// The time pills, with a marked time ("recommended", "last estimate") slotted in order when it isn't one of them
// (50m sits between 45m and 1h), so picking it is one click like the rest.
export const timePillValues = (markedMinutes: number | null | undefined): number[] =>
  markedMinutes == null || TIME_PRESETS.includes(markedMinutes)
    ? TIME_PRESETS
    : [...TIME_PRESETS, markedMinutes].sort((a, b) => a - b);

export const isPagePreset = (pages: number | null): boolean => pages != null && PAGE_PRESETS.includes(pages);

export const isRatePreset = (secondsPerPage: number): boolean => RATE_PRESETS_SECONDS.includes(secondsPerPage);

export type ParsedPages = {
  pages: number;
  from?: number;
  to?: number;
  rangeLabel: string;
};

export const parsePagesInput = (raw: string): ParsedPages | null => {
  const str = (raw || '').trim();
  if (!str) return null;
  const rangeMatch = str.match(/^(\d+)\s*-\s*(\d+)$/);
  if (rangeMatch) {
    const start = parseInt(rangeMatch[1], 10);
    const end = parseInt(rangeMatch[2], 10);
    if (end < start) return null;
    return { pages: end - start + 1, from: start, to: end, rangeLabel: `pp. ${start}–${end}` };
  }
  const num = parseInt(str, 10);
  if (!num || num <= 0) return null;
  return { pages: num, rangeLabel: `${num}p` };
};

/**
 * The time a reading of this many pages needs at its pace, as the server works it out once the pages are saved
 * (`TaskUtils.calculateTimeStillNeededForReading`, nothing read yet): rounded up to the minute.
 */
export const pagesToMinutes = (pages: number, secondsPerPage: number) => Math.ceil((pages * secondsPerPage) / 60);

export const pageRangesToDetected = (pageRanges?: PageRange[]) => {
  if (!pageRanges || pageRanges.length === 0) {
    return {
      pages: null as number | null,
      from: null as number | null,
      to: null as number | null,
      rangeLabel: null as string | null,
      rawPagesInput: null as string | null
    };
  }
  const totalPages = pageRanges.reduce((sum, range) => sum + (range.pages || 0), 0);
  if (totalPages <= 0) {
    return { pages: null, from: null, to: null, rangeLabel: null, rawPagesInput: null };
  }
  if (pageRanges.length === 1 && pageRanges[0].from != null && pageRanges[0].to != null) {
    const from = pageRanges[0].from;
    const to = pageRanges[0].to;
    return {
      pages: totalPages,
      from,
      to,
      rangeLabel: `pp. ${from}–${to}`,
      rawPagesInput: `${from}-${to}`
    };
  }
  return {
    pages: totalPages,
    from: null,
    to: null,
    rangeLabel: `${totalPages}p`,
    rawPagesInput: String(totalPages)
  };
};

const blankReadingFields = {
  pages: null as number | null,
  from: null as number | null,
  to: null as number | null,
  rangeLabel: null as string | null,
  rawPagesInput: null as string | null,
  detectedPages: null as number | null,
  detectedFrom: null as number | null,
  detectedTo: null as number | null,
  detectedRangeLabel: null as string | null,
  detectedRawPagesInput: null as string | null
};

export const plural = (count: number, word: string) => `${count} ${word}${count === 1 ? '' : 's'}`;

// Words that make a category's tasks tests rather than work to hand in, so the Estimator asks about studying for one
// rather than doing one. Gathered for Shovel's students (US and Canada, Europe, Australia) and its imports (syllabi,
// Canvas, Brightspace, Moodle): the usual English names, campus ones (Cornell's prelims, UK vivas and resits,
// Australian mid-sems), nursing and admissions tests, and the common words in the main European languages. Matched
// as whole words with accents dropped, so "Prüfungen" is "prufungen".
const TEST_WORDS = new Set([
  'quiz',
  'quizzes',
  'exam',
  'exams',
  'examination',
  'examinations',
  'test',
  'tests',
  'midterm',
  'midterms',
  'prelim',
  'prelims',
  'viva',
  'vivas',
  'orals',
  'mocks',
  'resit',
  'resits',
  'retake',
  'retakes',
  'osce',
  'osces',
  'hesi',
  'ati',
  'nclex',
  'teas',
  'mcat',
  'lsat',
  'gre',
  'gmat',
  // German, French, Spanish, Italian, Portuguese, Dutch, Czech, Polish
  'klausur',
  'klausuren',
  'prufung',
  'prufungen',
  'examen',
  'examens',
  'partiel',
  'partiels',
  'controle',
  'controles',
  'interro',
  'interros',
  'examenes',
  'parcial',
  'parciales',
  'prueba',
  'pruebas',
  'esame',
  'esami',
  'exame',
  'exames',
  'teste',
  'prova',
  'provas',
  'tentamen',
  'tentamens',
  'toets',
  'toetsen',
  'zkouska',
  'zkousky',
  'pisemka',
  'pisemky',
  'egzamin',
  'egzaminy',
  'kolokwium',
  'sprawdzian',
  'sprawdziany'
]);
const TEST_PHRASES = new Set([
  'mid term',
  'mid terms',
  'mid sem',
  'end sem',
  're sit',
  're sits',
  'knowledge check',
  'knowledge checks',
  'lab practical',
  'lab practicals',
  'practical exam'
]);
// A category named for work to hand in stays general even beside a test word: "Midterm Project", "Exams & Papers".
const WORK_WORDS = new Set([
  'assignment',
  'assignments',
  'homework',
  'homeworks',
  'hw',
  'project',
  'projects',
  'paper',
  'papers',
  'essay',
  'essays',
  'report',
  'reports',
  'set',
  'sets',
  'pset',
  'psets',
  'problems',
  'worksheet',
  'worksheets',
  'lab',
  'labs',
  'discussion',
  'discussions',
  'post',
  'posts',
  'presentation',
  'presentations',
  'journal',
  'journals',
  'portfolio',
  'portfolios',
  'exercise',
  'exercises',
  'response',
  'responses',
  'reflection',
  'reflections',
  'draft',
  'drafts',
  'submission',
  'submissions',
  'memo',
  'memos',
  'brief',
  'briefs',
  'study',
  'studies',
  'hausarbeit',
  'hausarbeiten',
  'hausaufgabe',
  'hausaufgaben',
  'devoir',
  'devoirs',
  'tarea',
  'tareas',
  'trabajo',
  'trabajos',
  'compito',
  'compiti',
  'opdracht',
  'opdrachten',
  'ukol',
  'ukoly',
  'zadanie',
  'zadania'
]);

/**
 * Whether a category's name says its tasks are tests (quizzes, exams, midterms…), so its time is time spent
 * studying. Each part of a combined name ("Quizzes & Exams", "Tests and Homework") is read on its own: a test word
 * anywhere in a part counts, and a part ending in work to hand in ("Midterm Project") makes the whole name general.
 * Anything unrecognised is general, which the Estimator's wording also covers.
 */
export const isTestCategory = (name: string): boolean => {
  const normalized = name
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
  const parts = normalized
    .split(/&|\+|\/|,|\band\b|\bund\b|\bet\b|\by\b/)
    .map(part => part.split(/[^a-z0-9]+/).filter(Boolean))
    .filter(words => words.length > 0);
  const hasTest = (words: string[]) =>
    words.some(word => TEST_WORDS.has(word)) ||
    words.some((word, i) => i > 0 && TEST_PHRASES.has(`${words[i - 1]} ${word}`)) ||
    (words.length === 1 && (words[0] === 'final' || words[0] === 'finals'));
  const isWork = (words: string[]) => WORK_WORDS.has(words[words.length - 1]);
  return parts.some(hasTest) && !parts.some(isWork);
};

// The time more than half of these tasks share, or null when none does.
export const sharedPriorMinutes = (tasks: EstimatorTask[]): number | null => {
  const counts = new Map<number, number>();
  tasks.forEach(task => counts.set(task.minutes!, (counts.get(task.minutes!) || 0) + 1));
  let shared: number | null = null;
  counts.forEach((count, minutes) => {
    if (count * 2 > tasks.length) shared = minutes;
  });
  return shared;
};

export const mapWorkloadToEstimator = (
  course: CalendarCourse,
  workloadTasks: WorkloadTask[]
): Pick<EstimatorState, 'categories' | 'sources' | 'tasks' | 'originals'> => {
  const courseTasks = workloadTasks.filter(
    task => task.courseId === course.id && task.state !== TaskState.COMPLETED && !task.deleted
  );

  const categories: EstimatorCategory[] = (course.taskCategories || []).map(category => ({
    id: category.id,
    name: category.name,
    emoji: category.emoji || DEFAULT_TASK_CATEGORY_EMOJI,
    defaultMinutes: categoryMinutesOf(category),
    priorMinutes: null
  }));

  const sourcesById = new Map<number, EstimatorReadingSource>();
  (course.readingSources || []).forEach(source => {
    sourcesById.set(source.id, {
      id: source.id,
      name: source.name,
      secondsPerPage: source.secondsPerPage || DEFAULT_SECONDS_PER_PAGE
    });
  });

  const tasks: EstimatorTask[] = courseTasks.map(task => {
    if (task.reading) {
      if (!sourcesById.has(task.reading.id)) {
        sourcesById.set(task.reading.id, {
          id: task.reading.id,
          name: task.reading.name,
          secondsPerPage: task.reading.secondsPerPage || DEFAULT_SECONDS_PER_PAGE
        });
      }
      const detected = pageRangesToDetected(task.pageRanges);
      const source = sourcesById.get(task.reading.id)!;
      return {
        id: task.id,
        title: task.title,
        kind: 'reading' as const,
        categoryId: task.taskCategory?.id ?? null,
        sourceId: task.reading.id,
        minutes: readingMinutes(detected.pages, source.secondsPerPage),
        overridden: false,
        overriddenThisSession: false,
        trashed: false,
        estimatedAtOpen: detected.pages != null,
        pages: detected.pages,
        from: detected.from,
        to: detected.to,
        rangeLabel: detected.rangeLabel,
        rawPagesInput: detected.rawPagesInput,
        detectedPages: detected.pages,
        detectedFrom: detected.from,
        detectedTo: detected.to,
        detectedRangeLabel: detected.rangeLabel,
        detectedRawPagesInput: detected.rawPagesInput
      };
    }

    const totalMinutes = (task.timeNeed || 0) + (task.timeSpent || 0);
    const minutes = totalMinutes > 0 ? totalMinutes : null;
    return {
      id: task.id,
      title: task.title,
      kind: 'regular' as const,
      categoryId: task.taskCategory?.id ?? null,
      sourceId: null,
      minutes,
      overridden: minutes != null,
      overriddenThisSession: false,
      trashed: false,
      estimatedAtOpen: minutes != null,
      ...blankReadingFields
    };
  });

  const sources = Array.from(sourcesById.values());
  const originals: EstimatorOriginals = {
    categories: Object.fromEntries(
      categories.map(category => [
        category.id,
        { name: category.name, emoji: category.emoji, minutes: category.defaultMinutes }
      ])
    ),
    sources: Object.fromEntries(
      sources.map(source => [source.id, { name: source.name, secondsPerPage: source.secondsPerPage }])
    ),
    titles: Object.fromEntries(tasks.map(task => [task.id, task.title]))
  };

  const categoriesWithPrior = categories.map(category => ({
    ...category,
    priorMinutes: sharedPriorMinutes(
      tasks.filter(task => task.kind === 'regular' && task.categoryId === category.id && task.minutes != null)
    )
  }));
  return {
    categories: categoriesWithPrior,
    sources,
    tasks,
    originals
  };
};

// What a save made elsewhere (the task's own dialog, opened from its row) changed about one task, for the open
// Estimator to take in. Only what changed between the task's two saved versions is carried over, so a time or category
// picked in the Estimator and not yet saved survives an unrelated edit, such as a new title.
export type ServerTaskChange = {
  id: number;
  remove?: boolean;
  replace?: EstimatorTask;
  source?: EstimatorReadingSource;
  patch?: Partial<EstimatorTask>;
};

export const serverTaskChange = (
  course: CalendarCourse,
  before: WorkloadTask,
  after: WorkloadTask | undefined
): ServerTaskChange | null => {
  const old = mapWorkloadToEstimator(course, [before]).tasks[0];
  const fresh = after ? mapWorkloadToEstimator(course, [after]) : null;
  const next = fresh?.tasks[0];
  // Completed, deleted or moved to another course: it's no longer this course's to estimate.
  if (!next) return { id: before.id, remove: true };
  if (!old) return null;

  // Turned into a reading or back, or moved to another book: what the Estimator held for it no longer applies.
  if (old.kind !== next.kind || old.sourceId !== next.sourceId) {
    return { id: next.id, replace: next, source: fresh!.sources.find(source => source.id === next.sourceId) };
  }

  const patch: Partial<EstimatorTask> = {};
  if (old.title !== next.title) patch.title = next.title;
  if (old.categoryId !== next.categoryId) patch.categoryId = next.categoryId;
  if (next.kind === 'regular' && old.minutes !== next.minutes) {
    patch.minutes = next.minutes;
    patch.overridden = next.overridden;
    patch.overriddenThisSession = false;
  }
  if (next.kind === 'reading' && (old.pages !== next.pages || old.from !== next.from || old.to !== next.to)) {
    Object.assign(patch, {
      pages: next.pages,
      from: next.from,
      to: next.to,
      rangeLabel: next.rangeLabel,
      rawPagesInput: next.rawPagesInput,
      detectedPages: next.detectedPages,
      detectedFrom: next.detectedFrom,
      detectedTo: next.detectedTo,
      detectedRangeLabel: next.detectedRangeLabel,
      detectedRawPagesInput: next.detectedRawPagesInput
    });
  }
  return Object.keys(patch).length > 0 ? { id: next.id, patch } : null;
};

export const buildEstimateRequest = (
  state: Pick<EstimatorState, 'categories' | 'sources' | 'tasks' | 'originals'>,
  courseId: number
): EstimateTasksRequest => {
  const categoryUpdates = state.categories
    .filter(category => {
      const original = state.originals.categories[category.id];
      return original && (original.name !== category.name || original.emoji !== category.emoji);
    })
    .map(category => ({ id: category.id, name: category.name, emoji: category.emoji }));

  const sourceUpdates = state.sources
    .filter(source => {
      const original = state.originals.sources[source.id];
      return original && (original.name !== source.name || original.secondsPerPage !== source.secondsPerPage);
    })
    .map(source => ({ id: source.id, name: source.name, secondsPerPage: source.secondsPerPage }));

  const titleUpdates = state.tasks
    .filter(task => state.originals.titles[task.id] !== undefined && state.originals.titles[task.id] !== task.title)
    .map(task => ({ id: task.id, title: task.title }));

  // A category's pick becomes its time (Petr, 2026-10-28). Reset all clears this visit's pick and leaves the saved
  // time as it was.
  const categoryMinutes: Record<number, number> = {};
  state.categories.forEach(category => {
    const original = state.originals.categories[category.id];
    if (category.defaultMinutes != null && category.defaultMinutes !== original?.minutes) {
      categoryMinutes[category.id] = Math.round(category.defaultMinutes);
    }
  });

  return {
    courseId,
    categoryUpdates,
    sourceUpdates,
    titleUpdates,
    batch: {
      courseId,
      tasks: state.tasks
        .filter(task => task.kind === 'regular')
        .map(task => ({
          id: task.id,
          categoryId: task.categoryId,
          minutes: task.minutes == null ? null : Math.round(task.minutes),
          trashed: task.trashed
        })),
      readings: state.tasks
        .filter(task => task.kind === 'reading')
        .map(task => ({
          id: task.id,
          sourceId: task.sourceId,
          categoryId: task.categoryId,
          pages: task.pages,
          ...(task.from != null ? { from: task.from } : {}),
          ...(task.to != null ? { to: task.to } : {}),
          trashed: task.trashed
        })),
      ...(Object.keys(categoryMinutes).length > 0 ? { categoryMinutes } : {})
    }
  };
};

export const isTaskEstimated = (task: EstimatorTask) =>
  task.kind === 'reading' ? task.pages != null : task.minutes != null;

export const addId = (ids: number[], id: number) => (ids.includes(id) ? ids : [...ids, id]);
export const removeId = (ids: number[], id: number) => ids.filter(value => value !== id);
export const toggleId = (ids: number[], id: number) => (ids.includes(id) ? removeId(ids, id) : addId(ids, id));

export const addKey = (keys: string[], key: string) => (keys.includes(key) ? keys : [...keys, key]);
export const removeKey = (keys: string[], key: string) => keys.filter(value => value !== key);
export const toggleKey = (keys: string[], key: string) => (keys.includes(key) ? removeKey(keys, key) : addKey(keys, key));

export const patchTask = (tasks: EstimatorTask[], id: number, patch: Partial<EstimatorTask>): EstimatorTask[] =>
  tasks.map(task => (task.id === id ? { ...task, ...patch } : task));
